import { Logo } from './Logo';
import { initials } from '../../../policies/initials';
import { useColorMode } from '../../theme/components/ColorModeProvider';
import { useOrganization } from './OrganizationProvider';
import { pickLogo } from '../policies/pick-logo';

/**
 * Where the logo sits. A tenant logo gets a box (max height × max width) rather than a
 * fixed height, so a wide two-line lockup and a square mark both fill it without
 * distorting; Chukua's own logo has fixed proportions, so it keeps a plain height.
 */
const SIZES = {
  sidebar: { box: { maxHeight: 40, maxWidth: 168 }, chukua: 22 },
  yard: { box: { maxHeight: 36, maxWidth: 160 }, chukua: 20 },
  login: { box: { maxHeight: 56, maxWidth: 240 }, chukua: 26 },
  panel: { box: { maxHeight: 64, maxWidth: 280 }, chukua: 26 },
} as const;

export type TenantLogoSize = keyof typeof SIZES;

interface TenantLogoProps {
  size: TenantLogoSize;
  /** Force the variant for a surface that is dark in both colour modes (login brand panel). */
  surface?: 'auto' | 'dark';
}

/** The tenant's logo, or its name set in type when it has none. Chukua's logo until the deployment has a company. */
export function TenantLogo({ size, surface = 'auto' }: TenantLogoProps) {
  const profile = useOrganization();
  const { mode } = useColorMode();
  const { box, chukua } = SIZES[size];
  const onDark = surface === 'dark' || mode === 'dark';

  if (!profile.configured) return <Logo height={chukua} tone={surface === 'dark' ? 'inverse' : 'auto'} />;

  const src = pickLogo(profile, onDark ? 'dark' : 'light');
  // Both max bounds with auto width/height: the browser scales down to fit the box, keeping the aspect ratio.
  if (src) return <img src={src} alt={profile.name} style={box} className="block h-auto w-auto" />;

  return (
    <span className="block truncate leading-none font-semibold tracking-tight" style={{ fontSize: Math.round(chukua * 0.8), maxWidth: box.maxWidth }}>
      {profile.name}
    </span>
  );
}

/** Square stand-in for the collapsed rail: the tenant's initials, or Chukua's mark. */
export function TenantMark() {
  const profile = useOrganization();
  if (!profile.configured) return <Logo variant="mark" height={16} />;
  return (
    <span
      aria-label={profile.name}
      className="grid size-8 place-items-center rounded-lg bg-primary/10 text-xs font-semibold text-primary ring-1 ring-primary/20"
    >
      {initials(profile.name)}
    </span>
  );
}
